import { useMemo, useState } from 'react';
import Button from '@components/ui/Button';
import DatePicker from '@components/ui/DatePicker';
import { useApiClient } from '@hooks/useApiClient';
import { toast } from '@utils/toast';
import {
    getUnsyncedOrders,
    deleteUnsyncedOrders,
    updateUnsyncedOrders,
} from '../../../../services/storeEventService';
import type { UnsyncedOrder } from '../../../../types/api.types';
import DeleteUnsyncedOrdersDialog from './DeleteUnsyncedOrdersDialog';
import ReassignUnsyncedOrdersDialog from './ReassignUnsyncedOrdersDialog';
import UnsyncedOrderRow from './UnsyncedOrderRow';
import type { ErrorTypeOption } from './unsyncedOrderUtils';

interface UnsyncedOrdersTabProps {
    storeId: string;
    storeBrandId: string;
}

type SubModal = 'delete' | 'reassign' | null;

export default function UnsyncedOrdersTab({ storeId, storeBrandId }: UnsyncedOrdersTabProps) {
    const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());

    const [orders, setOrders] = useState<UnsyncedOrder[] | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

    const [subModal, setSubModal] = useState<SubModal>(null);

    const [deleteErrorType, setDeleteErrorType] = useState<ErrorTypeOption | ''>('');
    const [forceDelete, setForceDelete] = useState(false);

    const [newStoreId, setNewStoreId] = useState('');
    const [newStoreBrandId, setNewStoreBrandId] = useState('');

    const {
        loading: fetchLoading,
        callApi: callFetch,
        clearError: clearFetchError,
    } = useApiClient({ toast: true });

    const {
        loading: actionLoading,
        callApi: callAction,
        clearError: clearActionError,
    } = useApiClient({ toast: true });

    const selectedOrders = useMemo(
        () => orders?.filter((order) => selectedIds.has(order._id)) ?? [],
        [orders, selectedIds]
    );

    const getDayRange = (date: Date) => {
        const start = new Date(date);
        start.setHours(0, 0, 0, 0);
        const end = new Date(date);
        end.setHours(23, 59, 59, 999);
        return { fromDateStr: start.toISOString(), toDateStr: end.toISOString() };
    };

    const handleFetch = async () => {
        clearFetchError();
        setOrders(null);
        setSelectedIds(new Set());
        setExpandedOrderId(null);

        const { fromDateStr, toDateStr } = getDayRange(selectedDate);

        await callFetch(
            () =>
                getUnsyncedOrders(
                    { storeId, fromDate: fromDateStr, toDate: toDateStr },
                    storeBrandId
                ),
            (result) => {
                setOrders(result);
                setSelectedIds(new Set());
                setExpandedOrderId(null);
            }
        );
    };

    const handleRefresh = async () => {
        const { fromDateStr, toDateStr } = getDayRange(selectedDate);

        const result = await callFetch(
            () =>
                getUnsyncedOrders(
                    { storeId, fromDate: fromDateStr, toDate: toDateStr },
                    storeBrandId
                )
        );
        if (result) {
            setOrders(result);
            setSelectedIds(new Set());
            setExpandedOrderId(null);
        }
    };

    const toggleSelectAll = () => {
        if (!orders) return;
        if (selectedIds.size === orders.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(orders.map((o) => o._id)));
        }
    };

    const toggleSelectOne = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleExpandOrder = (orderId: string) => {
        setExpandedOrderId((current) => (current === orderId ? null : orderId));
    };

    const openDeleteModal = () => {
        setDeleteErrorType('');
        setForceDelete(false);
        clearActionError();
        setSubModal('delete');
    };

    const openReassignModal = () => {
        setNewStoreId('');
        setNewStoreBrandId('');
        clearActionError();
        setSubModal('reassign');
    };

    const closeSubModal = () => {
        setSubModal(null);
    };

    const getSelectedCartIds = () => selectedOrders.map((order) => order.orderID);

    const handleDeleteConfirm = async () => {
        clearActionError();
        const cartIds = getSelectedCartIds();

        await callAction(
            () =>
                deleteUnsyncedOrders(
                    {
                        storeId,
                        cartIds,
                        ...(deleteErrorType ? { errorType: deleteErrorType } : {}),
                        forceDelete,
                    },
                    storeBrandId
                ),
            () => {
                closeSubModal();
                toast.success('Selected orders deleted.');
                handleRefresh();
            }
        );
    };

    const handleReassignConfirm = async () => {
        clearActionError();
        const cartIds = getSelectedCartIds();

        await callAction(
            () =>
                updateUnsyncedOrders(
                    {
                        storeId,
                        cartIds,
                        ...(newStoreId ? { newStoreId } : {}),
                        ...(newStoreBrandId ? { newStoreBrandId } : {}),
                    },
                    storeBrandId
                ),
            () => {
                closeSubModal();
                toast.success('Selected orders moved to the new store.');
                handleRefresh();
            }
        );
    };

    const selectedCount = selectedIds.size;
    const hasSelection = selectedCount > 0;
    const allSelected = orders !== null && orders.length > 0 && selectedIds.size === orders.length;

    return (
        <>
            <div className="space-y-5">
                <p className="text-sm text-[#64748b]">
                    Find orders that failed to sync to the system. Pick a date, then review,
                    delete, or move orders to another store.
                </p>

                <div className="space-y-4">
                    <DatePicker
                        label="Date"
                        value={selectedDate}
                        onChange={(date) => date && setSelectedDate(date)}
                        maxDate={new Date()}
                        placeholder="Select a date"
                    />
                    <Button
                        variant="primary"
                        isLoading={fetchLoading}
                        onClick={handleFetch}
                        className="w-full"
                    >
                        Find orders
                    </Button>
                </div>

                {orders !== null && (
                    <>
                        {orders.length === 0 ? (
                            <p className="text-sm text-[#64748b] text-center py-6">
                                No failed orders found for this date range.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        aria-label="Select all orders"
                                        checked={allSelected}
                                        onChange={toggleSelectAll}
                                        className="h-4 w-4 rounded border-[#cbd5e1] text-[#6e64f0] focus:ring-[#6e64f0]"
                                    />
                                    <span className="text-sm text-[#64748b]">
                                        {orders.length} failed order{orders.length !== 1 ? 's' : ''}
                                        {hasSelection && ` · ${selectedCount} selected`}
                                    </span>
                                </label>

                                {hasSelection && (
                                    <div className="sticky top-0 z-10 -mx-1 px-3 py-2.5 rounded-lg border border-[#e2e8f0] bg-[#fafbfc] shadow-sm">
                                        <div className="grid grid-cols-2 gap-2">
                                            <Button
                                                variant="destructive"
                                                size="small"
                                                onClick={openDeleteModal}
                                            >
                                                Delete selected
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="small"
                                                onClick={openReassignModal}
                                            >
                                                Move to store
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    {orders.map((order) => (
                                        <UnsyncedOrderRow
                                            key={order._id}
                                            order={order}
                                            isSelected={selectedIds.has(order._id)}
                                            isExpanded={expandedOrderId === order._id}
                                            onToggleSelect={() => toggleSelectOne(order._id)}
                                            onToggleExpand={() => toggleExpandOrder(order._id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <DeleteUnsyncedOrdersDialog
                isOpen={subModal === 'delete'}
                selectedOrders={selectedOrders}
                deleteErrorType={deleteErrorType}
                forceDelete={forceDelete}
                isLoading={actionLoading}
                onDeleteErrorTypeChange={setDeleteErrorType}
                onForceDeleteChange={setForceDelete}
                onConfirm={handleDeleteConfirm}
                onClose={closeSubModal}
            />

            <ReassignUnsyncedOrdersDialog
                isOpen={subModal === 'reassign'}
                selectedOrders={selectedOrders}
                newStoreId={newStoreId}
                newStoreBrandId={newStoreBrandId}
                isLoading={actionLoading}
                onNewStoreIdChange={setNewStoreId}
                onNewStoreBrandIdChange={setNewStoreBrandId}
                onConfirm={handleReassignConfirm}
                onClose={closeSubModal}
            />
        </>
    );
}
